import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Zap, Star, ArrowRight, Users, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

function PricingComponent() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(10);

  const currencySymbols = {
    USD: '$',
    GBP: '£'
  };

  const exchangeRate = 1.27; // 1 GBP = 1.27 USD (approximate)

  const pricingPlans = [
    {
      name: 'Basic',
      description: 'Perfect for small teams getting started',
      icon: Users,
      prices: {
        monthly: { USD: 2.53, GBP: 1.99 }, // per user
        annual: { USD: 2.27, GBP: 1.79 } // per user with discount
      },
      features: [
        { name: 'Basic scheduling', included: true },
        { name: 'Mobile apps', included: true },
        { name: 'Email support', included: true },
        { name: 'Time tracking & Timesheet', included: false },
        { name: 'Advanced analytics', included: false },
        { name: 'Custom integrations', included: false },
        { name: 'Dedicated account manager', included: false }
      ],
      cta: 'Start 7-Day Free Trial',
      popular: false,
      perUser: true
    },
    {
      name: 'Professional',
      description: 'Most popular for growing businesses',
      icon: Building2,
      prices: {
        monthly: { USD: 3.55, GBP: 2.79 }, // per user
        annual: { USD: 3.19, GBP: 2.51 } // per user with discount
      },
      features: [
        { name: 'Advanced scheduling', included: true },
        { name: 'Time tracking & Timesheet', included: true },
        { name: 'Mobile apps', included: true },
        { name: 'Priority email & chat support', included: true },
        { name: 'Advanced analytics & reporting', included: true },
        { name: 'Custom integrations', included: false },
        { name: 'Dedicated account manager', included: false }
      ],
      cta: 'Start 7-Day Free Trial',
      popular: true,
      perUser: true
    },
    {
      name: 'Enterprise',
      description: 'For large organizations with complex needs',
      icon: Star,
      customPrice: true,
      features: [
        { name: 'Enterprise scheduling & automation', included: true },
        { name: 'Advanced time tracking & Timesheet', included: true },
        { name: 'Mobile apps with white-label option', included: true },
        { name: '24/7 phone & priority support', included: true },
        { name: 'Advanced analytics & custom reports', included: true },
        { name: 'Custom integrations & SSO', included: true },
        { name: 'Dedicated account manager', included: true }
      ],
      cta: 'Contact Sales',
      popular: false,
      perUser: false
    }
  ];

  const getPrice = (plan) => {
    if (plan.customPrice) return 'Custom';
    const pricePerUser = plan.prices[billingCycle][selectedCurrency];
    return `${currencySymbols[selectedCurrency]}${pricePerUser.toFixed(2)}`;
  };

  const getTotalPrice = (plan, employees) => {
    if (plan.customPrice) return null;
    const pricePerUser = plan.prices[billingCycle][selectedCurrency];
    let total;
    if (billingCycle === 'annual') {
      // For annual billing, show total for 12 months
      total = pricePerUser * employees * 12;
    } else {
      // For monthly billing, show monthly total
      total = pricePerUser * employees;
    }
    return `${currencySymbols[selectedCurrency]}${total.toFixed(2)}`;
  };

  const getSavings = () => {
    return 30; // 30% discount on annual billing
  };

  return (
    <>
      <LandingNav />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, Competitive Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Get more features for less. Save up to 20% compared to competitors with our transparent pricing.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className={`text-lg font-semibold ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-8 w-14 items-center rounded-full bg-blue-600 transition-colors focus:outline-none"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-lg font-semibold ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
              </span>
              {billingCycle === 'annual' && (
                <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                  Save 30%
                </span>
              )}
            </div>

            {/* Currency Selector */}
            <div className="flex justify-center gap-2">
              {['USD', 'GBP'].map((currency) => (
                <button
                  key={currency}
                  onClick={() => setSelectedCurrency(currency)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedCurrency === currency
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {currency}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Employee Counter for Basic & Professional */}
          {selectedPlan && selectedPlan !== 'Enterprise' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 max-w-md mx-auto bg-white rounded-2xl shadow-lg p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
                Calculate Your Total Cost
              </h3>
              <div className="flex items-center justify-between mb-4">
                <label className="text-gray-700 font-semibold">Number of Employees:</label>
                <input
                  type="number"
                  min="1"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-24 px-4 py-2 border-2 border-blue-600 rounded-lg text-center font-bold text-gray-900"
                />
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Per user cost:</span>
                  <span className="font-semibold text-gray-900">
                    {getPrice(pricingPlans.find(p => p.name === selectedPlan))}/mo
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Employees:</span>
                  <span className="font-semibold text-gray-900">{employeeCount}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Billing cycle:</span>
                  <span className="font-semibold text-gray-900">{billingCycle === 'monthly' ? 'Monthly' : 'Yearly (12 months)'}</span>
                </div>
                <div className="border-t-2 border-blue-200 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total:</span>
                    <span className="text-2xl font-extrabold text-blue-600">
                      {getTotalPrice(pricingPlans.find(p => p.name === selectedPlan), employeeCount)}
                      {billingCycle === 'monthly' ? '/mo' : '/yr'}
                    </span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-green-600 font-semibold mt-2 text-center">
                      You save 30% with annual billing!
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {pricingPlans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all hover:shadow-2xl hover:scale-105 ${
                    plan.popular ? 'border-4 border-blue-600' : 'border border-gray-200'
                  }`}
                  onClick={() => setSelectedPlan(plan.name)}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm font-bold rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6 mx-auto">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-center mb-6">{plan.description}</p>
                    
                    <div className="text-center mb-6">
                      {plan.customPrice ? (
                        <div>
                          <div className="text-4xl font-extrabold text-gray-900 mb-2">Custom Pricing</div>
                          <p className="text-sm text-gray-600">Contact us for a tailored solution</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-baseline justify-center">
                            <span className="text-5xl font-extrabold text-gray-900">{getPrice(plan)}</span>
                            <span className="text-gray-600 ml-2">/user/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-sm text-green-600 font-semibold mt-2">
                              Save {getSavings()}% with annual billing
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    
                    <Link to={plan.cta === 'Contact Sales' ? '/contact' : '/signup'}>
                      <button className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                        plan.popular
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}>
                        {plan.cta}
                        <ArrowRight className="inline-block ml-2 w-5 h-5" />
                      </button>
                    </Link>
                    
                    <div className="mt-8 space-y-4">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mr-3 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                            {feature.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Comparison Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-xl"
          >
            <Zap className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">Why Choose Shiftry?</h3>
            <p className="text-xl mb-6 max-w-3xl mx-auto">
              Our prices are 20-30% lower than competitors like When I Work, Deputy, and Homebase, while offering more features and better support.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-2xl font-bold mb-1">20-30%</p>
                <p className="text-sm">Lower than competitors</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-2xl font-bold mb-1">7-Day</p>
                <p className="text-sm">Free trial, no credit card</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-2xl font-bold mb-1">24/7</p>
                <p className="text-sm">Mai AI Support available</p>
              </div>
            </div>
          </motion.div>

          {/* FAQ or Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Need a custom plan?</h3>
            <p className="text-gray-600 mb-6">We offer flexible pricing for enterprises with 500+ employees</p>
            <Link to="/contact">
              <button className="px-8 py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-all">
                Contact Sales Team
              </button>
            </Link>
          </motion.div>
        </div>
      </div>
      
      <FooterLanding />
    </>
  );
}

export default PricingComponent;
